import { getJSON } from 'common/services/api'
import { useQueries, useQuery } from 'react-query'
import { getQueryParams } from 'utils/getQueryParams'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { REVISIONS_QUERY_KEY, REVISIONS_URL } from './queryConstants'
import { YEAR_FIRST_DATE_FORMAT } from '../utils.datetime'
import { RotaEventInstance } from '../types'
import { useMemo } from 'react'

dayjs.extend(isoWeek)

const RECURSIVE_LIMIT = 100

type UseRecursiveRevisionsListReturn = {
  revisions: RotaEventInstance[]
  isLoading: boolean
  metadata: { unassignedHours: number }
  errorCode: number
}

type UseRecursionRevisionsListProps = {
  selectedDate?: Date
  intervalType?: 'daily' | 'weekly'
  otherFilterParams?: Record<string, any>
  totalEvents?: number
  recursionEnabled?: boolean
}

const useRecursionRevisionsList = ({
  selectedDate,
  intervalType,
  otherFilterParams = {},
  totalEvents,
  recursionEnabled = false,
}: UseRecursionRevisionsListProps = {}): UseRecursiveRevisionsListReturn => {
  const hasTotalEventsDefined = totalEvents !== undefined

  const isRecursiveFetch =
    recursionEnabled && hasTotalEventsDefined && totalEvents >= RECURSIVE_LIMIT

  const date =
    intervalType === 'weekly'
      ? dayjs(selectedDate).startOf('isoWeek').format(YEAR_FIRST_DATE_FORMAT)
      : dayjs(selectedDate).format(YEAR_FIRST_DATE_FORMAT)

  const params = getQueryParams({
    date,
    intervalType,
    ...otherFilterParams,
  })

  const { isLoading, data: revisions } = useQuery(
    [REVISIONS_QUERY_KEY, params, otherFilterParams.siteShift],
    () =>
      getJSON({
        url: `${REVISIONS_URL}?${params}`,
        isRota: true,
        customErrorHandling: true,
      }),
    {
      enabled:
        (isRecursiveFetch ? hasTotalEventsDefined : true) &&
        !!selectedDate &&
        !!intervalType &&
        !isRecursiveFetch,
      retry: false,
    }
  )

  // Recursive logic
  const loops = hasTotalEventsDefined
    ? Math.ceil(totalEvents / RECURSIVE_LIMIT)
    : 1

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const queries = Array.from({ length: loops }).map((_, index) => {
    const recursiveParams = getQueryParams({
      date,
      intervalType,
      ...otherFilterParams,
      page: index + 1,
      limit: RECURSIVE_LIMIT,
    })

    return {
      queryKey: [
        REVISIONS_QUERY_KEY,
        recursiveParams,
        otherFilterParams.siteShift,
      ],
      queryFn: () =>
        getJSON({
          url: `${REVISIONS_URL}?${recursiveParams}`,
          isRota: true,
        }),
      enabled:
        hasTotalEventsDefined &&
        !!selectedDate &&
        !!intervalType &&
        isRecursiveFetch,
    }
  })

  const recursiveResults = useQueries(queries)

  const recursiveData = useMemo(() => {
		const data = recursiveResults.map((result) => result?.data?.data || []).flat()

		const onlyDefinedData = data.filter((item) => item !== undefined)

		return onlyDefinedData
  }, [recursiveResults])

  const singleQueryData = revisions?.data || []

  return {
    revisions: !isRecursiveFetch ? singleQueryData : recursiveData,
    // We check only for first batch Loading
    isLoading: !isRecursiveFetch
      ? !!isLoading
      : recursiveResults?.[0]?.isLoading,
    metadata: revisions?.metadata,
    errorCode: revisions?.statusCode,
  }
}

export default useRecursionRevisionsList
